import { NavLink } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { SidebarGroup } from './app-sidebar-groups';

/**
 * BL-297: eine kollabierbare Gruppe der neuen Sidebar (Header + Chevron,
 * Item-Liste, optional eine Chip-Reihe für "kompakte" Unterpunkte wie die
 * Kanzlei-Auswertungen FiBu/LoBu/OPOS/USt-VA). Ausgelagert aus
 * `app-sidebar.tsx` wegen der BL-112-300-Zeilen-Schranke.
 */
export function AppSidebarGroup({
  group,
  open,
  onToggle,
}: {
  group: SidebarGroup;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="py-1">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-center gap-1.5 px-3 py-1.5 text-left text-[11px] font-semibold uppercase tracking-wide text-ink-muted hover:text-ink"
      >
        {open ? (
          <ChevronDown className="h-3 w-3 shrink-0" />
        ) : (
          <ChevronRight className="h-3 w-3 shrink-0" />
        )}
        <span className="flex-1">{group.header}</span>
      </button>

      {open ? (
        <div className="space-y-0.5 px-3 pb-1">
          {group.items.map(({ to, label, icon: Icon, badge, badgeTone }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              className={({ isActive }) =>
                cn(
                  'group flex items-center gap-3 px-2.5 py-[7px] text-[13px] transition-colors',
                  'border-l-2',
                  isActive
                    ? 'border-avameo-primary bg-avameo-primary/5 text-avameo-primary'
                    : 'border-transparent text-ink-soft hover:bg-bg-alt hover:text-ink',
                )
              }
            >
              <Icon className="h-[16px] w-[16px] shrink-0" />
              <span className="flex-1">{label}</span>
              {badge && (
                <span
                  className={cn(
                    'px-1.5 py-[1px] text-[10px] font-medium',
                    badgeTone === 'danger'
                      ? 'bg-destructive text-white'
                      : 'bg-hair text-ink-soft group-[.active]:bg-avameo-primary group-[.active]:text-white',
                  )}
                >
                  {badge}
                </span>
              )}
            </NavLink>
          ))}

          {/* BL-297: FiBu/LoBu/OPOS/USt-VA als kompakte Pill-Reihe statt vier
              eigener Vollbreite-Zeilen -- das war der konkrete Platz-Einwand
              aus dem User-Feedback zur alten Kanzlei-Sektion. */}
          {group.chipItems.length > 0 && (
            <div className="flex flex-wrap gap-1.5 px-0.5 pt-1.5">
              {group.chipItems.map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  className={({ isActive }) =>
                    cn(
                      'inline-flex items-center gap-1.5 border px-2 py-1 text-[12px] transition-colors',
                      isActive
                        ? 'border-avameo-primary bg-avameo-primary/5 text-avameo-primary'
                        : 'border-hair text-ink-soft hover:bg-bg-alt hover:text-ink',
                    )
                  }
                >
                  <Icon className="h-3.5 w-3.5 shrink-0" />
                  {label}
                </NavLink>
              ))}
            </div>
          )}
        </div>
      ) : (
        // Eingeklappte Gruppe (Default bei "System"): Vorschau-Zeile statt
        // leerer Fläche -- macht sichtbar, was drinsteckt, ohne aufzuklappen.
        <button
          type="button"
          onClick={onToggle}
          className="block w-full truncate px-6 pb-1.5 text-left text-[12px] text-ink-muted hover:text-ink"
        >
          {[...group.items, ...group.chipItems].map((i) => i.label).join(', ')}
        </button>
      )}
    </div>
  );
}
